/**
 * ChipListInput — a list of short strings rendered as removable chips with an
 * Enter-to-add input underneath. Admin-only (plain English). Normalization and
 * validation are the caller's job via `normalize` / `validate`; this component
 * only dedupes.
 */

import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  /** Shown when the list is empty. */
  emptyText?: string;
  /** Applied to the draft before adding (e.g. lowercase, strip "@"). */
  normalize?: (raw: string) => string;
  /** Return an error message to refuse the add, or null to accept. */
  validate?: (normalized: string) => string | null;
  /** Monospace chips suit domains and ids. */
  mono?: boolean;
  inputWidth?: number;
}

export const ChipListInput: React.FC<Props> = ({
  value, onChange, disabled = false, placeholder, maxLength = 253, emptyText,
  normalize, validate, mono = true, inputWidth = 360,
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const normalized = (normalize ? normalize(draft) : draft).trim();
    if (!normalized) return;
    if (value.includes(normalized)) { setDraft(''); setError(null); return; }
    const err = validate ? validate(normalized) : null;
    if (err) { setError(err); return; }
    onChange([...value, normalized]);
    setDraft('');
    setError(null);
  };

  const remove = (item: string) => onChange(value.filter(v => v !== item));

  return (
    <div>
      <div className="d-flex flex-wrap gap-1 mb-2">
        {value.length === 0 ? (
          emptyText ? <em className="text-muted small">{emptyText}</em> : null
        ) : (
          value.map(item => (
            <span
              key={item}
              className="badge text-bg-light border d-inline-flex align-items-center gap-1"
              style={{ fontSize: '0.85em', padding: '6px 10px' }}
            >
              {mono ? <code className="text-dark">{item}</code> : <span className="text-dark">{item}</span>}
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-danger"
                aria-label={`Remove ${item}`}
                style={{ lineHeight: 1 }}
                disabled={disabled}
                onClick={() => remove(item)}
              >
                <X size={14} />
              </button>
            </span>
          ))
        )}
      </div>

      <div className="input-group input-group-sm" style={{ maxWidth: inputWidth }}>
        <input
          type="text"
          className={`form-control${error ? ' is-invalid' : ''}`}
          placeholder={placeholder}
          value={draft}
          maxLength={maxLength}
          disabled={disabled}
          onChange={e => { setDraft(e.target.value); if (error) setError(null); }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
          disabled={disabled || !draft.trim()}
          onClick={add}
        >
          <Plus size={14} /> Add
        </button>
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default ChipListInput;
